import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AddIcon, EditIcon } from '../AdminIcon';
import Slider from '../Slider/Slider';
import DataTable from '../DataTable/DataTable';
import ResourceTabs from '../ResourceTabs/ResourceTabs';
import SessionDurationLabel from '../SessionDurationLabel';
import PurposeEditorDialog from '../PurposeEditorDialog';
import Backend from '../../../utils/api';
import { useDocumentTitle } from '../../../utils/useDocumentTitle';

const columns = [
  { title: 'Check-in purpose', field: 'custom_reason' },
  { title: <SessionDurationLabel />, sorting: false, field: 'time_interval', type: 'numeric',
    render: row => {
      const duration = Number(row.time_interval) || 0;
      return `${String(Math.floor(duration / 60)).padStart(2, '0')}:${String(duration % 60).padStart(2, '0')}`;
    } },
  { title: 'Email reminder', field: 'enable_email_reminder', sorting: false,
    render: row => (row.enable_email_reminder === false ? 'Off' : 'On') },
  { title: 'Time tracking', field: 'enable_time_tracking', sorting: false,
    render: row => (row.enable_time_tracking === false ? 'Off' : 'On') },
];

export default function CustomReasonsTab({ user, labId, revert }) {
  useDocumentTitle('Admin: Check-in purposes – Pinpoint');
  const [editor, setEditor] = useState(null);
  const [revision, setRevision] = useState(0);
  const returnFocusRef = useRef(null);
  const activeTabRef = useRef(null);
  const activeDataFunction = useCallback(async () => {
    const response = await Backend.getReasons(user.UID, labId);
    if (response.error || !response.items) return { status: 0, items: [] };
    return { ...response, items: response.items.filter(row => !Number(row.isDeleted)) };
  }, [user.UID, labId, revision]);
  const archivedDataFunction = useCallback(async () => {
    const response = await Backend.getReasons(user.UID, labId, true);
    if (response.error || !response.items) return { status: 0, items: [] };
    return { ...response, items: response.items.filter(row => Number(row.isDeleted)) };
  }, [user.UID, labId, revision]);
  const openEditor = (event, purpose = null) => {
    returnFocusRef.current = event.currentTarget;
    setEditor({ purpose });
  };
  const editAction = row => ({ icon: () => <EditIcon />, tooltip: 'Edit check-in purpose', onClick: event => openEditor(event, row) });
  const archiveAction = archived => row => ({ icon: archived ? 'unarchive' : 'archive',
    tooltip: `${archived ? 'Unarchive' : 'Archive'} check-in purpose`,
    onClick: () => Backend.toggleCustomReasonArchive(user.UID, row.id, row.custom_reason, row.time_interval, !archived) });

  return <Slider name="Check-in purposes" slide index={0} onClick={() => revert(1)}>
    <ResourceTabs activeTabRef={activeTabRef}>
      <DataTable key="active" title="Check-in purposes" columns={columns} dataFunction={activeDataFunction}
        customActions={[
          { icon: () => <AddIcon />, tooltip: 'Add check-in purpose', isFreeAction: true, onClick: event => openEditor(event) },
          editAction, archiveAction(false),
        ]} />
      <DataTable key="archived" title="Archived check-in purposes" columns={columns} dataFunction={archivedDataFunction}
        customActions={[editAction, archiveAction(true)]} />
    </ResourceTabs>
    {editor && <PurposeEditorDialog purpose={editor.purpose} user={user} labId={labId}
      finalFocusRef={returnFocusRef} onClose={() => setEditor(null)} onSaved={() => {
        setEditor(null);
        setRevision(value => value + 1);
      }} />}
  </Slider>;
}

CustomReasonsTab.propTypes = {
  user: PropTypes.object.isRequired, labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired, revert: PropTypes.func.isRequired,
};
